import React, { Suspense, lazy } from 'react'
import {
    Box,
    Container,
    Typography,
    Button,
    Chip,
    Card,
    CardContent,
    Grid,
    Stack,
    AppBar,
    Toolbar,
    Link as MuiLink,
    Divider,
    Avatar,
    AvatarGroup,
} from '@mui/material'
import { ThemeProvider, alpha, createTheme } from '@mui/material/styles'
import palette from '~shared/theme/palette'
import typography from '~shared/theme/typography'
import shadows from '~shared/theme/shadows'
import customShadows from '~shared/theme/customShadows'
import componentsOverride from '~shared/theme/overrides'
import CssBaseline from '@mui/material/CssBaseline'
import SportBasketballIcon from '@mui/icons-material/SportsBasketball'
import AppleIcon from '@mui/icons-material/Apple'
import AndroidIcon from '@mui/icons-material/Android'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import BalanceIcon from '@mui/icons-material/Balance'
import BarChartIcon from '@mui/icons-material/BarChart'
import EventIcon from '@mui/icons-material/Event'
import GroupIcon from '@mui/icons-material/Group'
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import DiamondIcon from '@mui/icons-material/Diamond'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
// Yandex Map loads only on the client — renderToString shows the fallback placeholder
const YandexMapBlock = lazy(() => import('./YandexMapBlock.jsx'))
// ELO chart uses recharts — skip SSR to avoid heavy JS on server
const EloChartBlock = lazy(() => import('./EloChartBlock.jsx'))

// Build theme from shared SPA pieces, override palette for dark city page
const _themeOptions = {
    palette: {
        ...palette,
        mode: 'dark',
        background: { default: '#0a0a0a', paper: '#141414' },
        text: { primary: '#ffffff', secondary: 'rgba(255,255,255,0.6)' },
    },
    shape: { borderRadius: 4 },
    typography,
    shadows: shadows(),
    customShadows: customShadows(),
}
const theme = createTheme(_themeOptions)
theme.components = {
    ...componentsOverride(theme),
    MuiContainer: {
        styleOverrides: {
            root: {
                [theme.breakpoints.up('lg')]: {
                    maxWidth: '1280px',
                    paddingLeft: theme.spacing(3),
                    paddingRight: theme.spacing(3),
                },
                [theme.breakpoints.up('md')]: {
                    maxWidth: '1100px',
                    paddingLeft: theme.spacing(3),
                    paddingRight: theme.spacing(3),
                },
                [theme.breakpoints.down('md')]: {
                    maxWidth: '100%',
                    paddingLeft: theme.spacing(2),
                    paddingRight: theme.spacing(2),
                },
            },
        },
    },
    MuiCssBaseline: {
        styleOverrides: `
            @font-face {
                font-family: 'Bebas Neue';
                src: url('/Bebas_Neue.ttf') format('truetype');
                font-display: swap;
            }
            @font-face {
                font-family: 'Manrope';
                font-weight: 400;
                src: url('/Manrope-Regular.ttf') format('truetype');
                font-display: swap;
            }
            @font-face {
                font-family: 'Manrope';
                font-weight: 600;
                src: url('/Manrope-SemiBold.ttf') format('truetype');
                font-display: swap;
            }
            @font-face {
                font-family: 'Manrope';
                font-weight: 700;
                src: url('/Manrope-Bold.ttf') format('truetype');
                font-display: swap;
            }
            html, body { background: #0a0a0a; margin: 0; }
            @keyframes float {
                0%        { transform: translateY(0);     animation-timing-function: ease-out; }
                10%       { transform: translateY(-60px); animation-timing-function: ease-in; }
                20%       { transform: translateY(0);     animation-timing-function: ease-out; }
                30%       { transform: translateY(-40px); animation-timing-function: ease-in; }
                40%       { transform: translateY(0);     animation-timing-function: ease-out; }
                50%       { transform: translateY(-25px); animation-timing-function: ease-in; }
                60%, 100% { transform: translateY(0); }
            }
            @keyframes marquee-left {
                from { transform: translateX(0); }
                to   { transform: translateX(-50%); }
            }
            @keyframes marquee-right {
                from { transform: translateX(-50%); }
                to   { transform: translateX(0); }
            }
        `,
    },
}

const _USERS = [
    { id: 39, first_name: 'Иван', last_name: 'Кошевой', icon: 'https://t.me/i/userpic/320/1A7YCjlbDiZCOkcJfUNYexDnlRqE63m9LW1fuorl9T0.jpg' },
    { id: 11, first_name: 'Данил', last_name: 'Балдин', icon: 'https://t.me/i/userpic/320/i5cRFlvUeHyZqBIkOvzfnj4LZ4YO2ygqNzoy-u6Ad6Q.jpg' },
    { id: 100, first_name: 'Анатолич', last_name: '', icon: 'https://t.me/i/userpic/320/fSvv0MgY1Hcnaqw-uczsy93oAiIIkSF9r-Xsr7bGZQU.jpg' },
    { id: 120, first_name: 'Леброн', last_name: 'Джеймс', icon: 'https://t.me/i/userpic/320/GBIjrxoAmyO3VqAB2b1JDs8mgsxHkmAQQMWySxcxUVw.jpg' },
    { id: 119, first_name: 'Frol', last_name: 'Ryzheborodov', icon: 'https://sun1-92.userapi.com/s/v1/ig2/qRsvkgzJfELLQN2vJvzjAg8wbKYyNy1FFJsAxFTO9nFFBMygrtHaW1Rwz7w4ffzfVOfxgw7M9Ny7l3ES4einMwPa.jpg?quality=95&crop=0,32,452,452&cs=200x200' },
]

const LEAGUES = [
    { id: 1, name: '1x1', subtitle: 'Индивидуальные соревнования', primary_color: '#3B82F6', users: _USERS, top: { ..._USERS[0], rating_value: 1245 } },
    { id: 2, name: '3x3', subtitle: 'Командные баталии', primary_color: '#22C55E', users: _USERS, top: { ..._USERS[1], rating_value: 1182 } },
    { id: 3, name: '5x5', subtitle: 'Классический баскетбол', primary_color: '#FF5101', users: _USERS, top: { ..._USERS[3], rating_value: 1310 } },
    { id: 4, name: 'Street', subtitle: 'Стрит-баскетбол', primary_color: '#A855F7', users: _USERS, top: { ..._USERS[4], rating_value: 988 } },
    { id: 5, name: 'Pro', subtitle: 'Только лучшие', primary_color: '#F59E0B', users: _USERS, top: { ..._USERS[2], rating_value: 1521 } },
]

const TEAMS = [
    { id: 1, name: 'Red Bulls', city_name: 'Москва', city_type: 'г', avatar_url: null },
    { id: 2, name: 'Чёрные Орлы', city_name: 'Санкт-Петербург', city_type: 'г', avatar_url: null },
    { id: 3, name: 'Степные Волки', city_name: 'Казань', city_type: 'г', avatar_url: null },
    { id: 4, name: 'Пламя', city_name: 'Екатеринбург', city_type: 'г', avatar_url: null },
    { id: 5, name: 'Тигры', city_name: 'Новосибирск', city_type: 'г', avatar_url: null },
    { id: 6, name: 'Балтика', city_name: 'Калининград', city_type: 'г', avatar_url: null },
    { id: 7, name: 'Химки Хупс', city_name: 'Химки', city_type: 'г', avatar_url: null },
    { id: 8, name: 'Street Kings', city_name: 'Москва', city_type: 'г', avatar_url: null },
    { id: 9, name: 'Дружина', city_name: 'Краснодар', city_type: 'г', avatar_url: null },
    { id: 10, name: 'Восток', city_name: 'Владивосток', city_type: 'г', avatar_url: null },
    { id: 11, name: 'Арена 48', city_name: 'Самара', city_type: 'г', avatar_url: null },
    { id: 12, name: 'Форвард', city_name: 'Ростов-на-Дону', city_type: 'г', avatar_url: null },
    { id: 13, name: 'Лакеры Урал', city_name: 'Челябинск', city_type: 'г', avatar_url: null },
    { id: 14, name: 'Нева Хупс', city_name: 'Санкт-Петербург', city_type: 'г', avatar_url: null },
    { id: 15, name: 'Thunder Boys', city_name: 'Нижний Новгород', city_type: 'г', avatar_url: null },
    { id: 16, name: 'Сибирь', city_name: 'Омск', city_type: 'г', avatar_url: null },
]

export default function CityPage({ city, cityName, cityNameNom, courts = [], rental_courts = [], points = [], lat, lng }) {

    const webAppUrl = 'https://hoopbalance.ru'
    const appStoreUrl = 'https://apps.apple.com/ru/app/hoopbalance/id6745330223' // TODO: replace
    const googlePlayUrl = 'https://play.google.com/store/apps/details?id=com.hoopbalance.apphoopbalance&pli=1' // TODO: replace

    const isInfo = city === 'info'
    const displayNom = isInfo ? null : (cityNameNom || cityName || city)   // nominative: "Москва"
    const displayLoc = isInfo ? null : (cityName || cityNameNom || city)   // locative:   "Москве"

    const _schemaOrg = {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'HoopBalance',
        url: 'https://hoopbalance.ru',
        logo: 'https://hoopbalance.ru/logo.png',
        sameAs: [appStoreUrl, googlePlayUrl],
    }
    const _schemaApp = {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'HoopBalance',
        operatingSystem: 'iOS, Android',
        applicationCategory: 'SportsApplication',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'RUB' },
        downloadUrl: appStoreUrl,
        description: `Приложение для баскетбола${displayNom ? ` в ${displayNom}` : ''} — рейтинг, мероприятия, площадки, команды`,
    }
    const _schemaBreadcrumb = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'HoopBalance', item: 'https://hoopbalance.ru' },
            { '@type': 'ListItem', position: 2, name: displayNom ? `Баскетбол в ${displayNom}` : 'Баскетбол', item: `https://hoopbalance.ru/${city}` },
        ],
    }
    const _schemaCourts = courts.length > 0 ? {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: displayNom ? `Баскетбольные площадки в ${displayNom}` : 'Баскетбольные площадки',
        itemListElement: courts.map((c, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            item: {
                '@type': 'SportsActivityLocation',
                name: c.name,
                ...(c.address && { address: { '@type': 'PostalAddress', streetAddress: c.address } }),
                ...(c.full_name && !c.address && { address: { '@type': 'PostalAddress', streetAddress: c.full_name } }),
                ...(c.lat && c.lng && { geo: { '@type': 'GeoCoordinates', latitude: c.lat, longitude: c.lng } }),
                ...(c.images?.[0] && { image: c.images[0] }),
            },
        })),
    } : null

    const handleDownload = () => {
        const ua = navigator.userAgent
        if (/iPad|iPhone|iPod/.test(ua)) {
            window.location.href = appStoreUrl
        } else {
            window.location.href = googlePlayUrl
        }
    }

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <JsonLd data={_schemaOrg} />
            <JsonLd data={_schemaApp} />
            <JsonLd data={_schemaBreadcrumb} />
            {_schemaCourts && <JsonLd data={_schemaCourts} />}

            {/* ── HEADER ── */}
            <AppBar
                position="sticky"
                elevation={0}
                sx={{
                    bgcolor: 'rgba(10,10,10,0.92)',
                    backdropFilter: 'blur(12px)',
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                }}
            >
                <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 2, md: 5 } }}>
                    <Stack direction="row" alignItems="center" gap={1} component="a" href={webAppUrl}
                        sx={{ textDecoration: 'none', color: 'inherit' }}
                    >
                        <Typography variant="h3">
                            Hoop<Box component="span" sx={{ color: 'primary.main' }}>Balance</Box>
                        </Typography>
                    </Stack>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={handleDownload}
                        sx={{ borderRadius: 2 }}
                    >
                        Скачать приложение
                    </Button>
                </Toolbar>
            </AppBar>

            {/* ── HERO ── */}
            <Box
                component="section"
                sx={{
                    minHeight: '85vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: 'radial-gradient(ellipse 80% 60% at 70% 50%, rgba(255,81,1,0.12) 0%, transparent 60%)',
                    px: { xs: 2, md: 8 },
                    py: { xs: 8, md: 10 },
                    maxWidth: 1280,
                    mx: 'auto',
                    gap: 4,
                }}
            >
                <Box sx={{ flex: 1, maxWidth: 620 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            flexWrap: 'wrap',
                            mb: 3,
                        }}
                        gap={1}
                    >
                        {displayNom && (
                            <Chip
                                label={displayNom}
                                size="small"
                                sx={{
                                    bgcolor: alpha('#FF5101', 0.15),
                                    border: '1px solid',
                                    borderColor: alpha('#FF5101', 0.3),
                                    color: 'primary.main',
                                    fontWeight: 600,
                                    letterSpacing: '0.05em',
                                    textTransform: 'uppercase',
                                    fontSize: '0.72rem',
                                }}
                            />
                        )}
                        <Chip
                            label={`Любительский баскетбол`}
                            size="small"
                            sx={{
                                bgcolor: alpha('#FF5101', 0.15),
                                border: '1px solid',
                                borderColor: alpha('#FF5101', 0.3),
                                color: 'primary.main',
                                fontWeight: 600,
                                letterSpacing: '0.05em',
                                textTransform: 'uppercase',
                                fontSize: '0.72rem',
                            }}
                        />
                        <Chip
                            label={`Твой рейтинг`}
                            size="small"
                            sx={{
                                bgcolor: alpha('#FF5101', 0.15),
                                border: '1px solid',
                                borderColor: alpha('#FF5101', 0.3),
                                color: 'primary.main',
                                fontWeight: 600,
                                letterSpacing: '0.05em',
                                textTransform: 'uppercase',
                                fontSize: '0.72rem',
                            }}
                        />
                    </Box>
                    <Typography variant="h1" sx={{ fontSize: { xs: '3.5rem', md: '6rem' }, lineHeight: 1, mb: 3 }}>
                        {displayLoc ? (
                            <>
                                Баскетбол
                                <Box component="span" sx={{ color: 'primary.main', display: 'block' }}>
                                    в {displayLoc}
                                </Box>
                            </>
                        ) : (
                            <>
                                Приложение для
                                <Box component="span" sx={{ color: 'primary.main', display: 'block' }}>
                                    баскетбола
                                </Box>
                            </>
                        )}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 5, maxWidth: 480, lineHeight: 1.7 }}>
                        {displayLoc
                            ? `Разнообразим ваш баскетбол, играй и соревнуйся с друзьями за рейтинг в лигах, находи других игроков рядом, создавай игры и турниры в своём городе. Все данные о твоём баскетболе в одном приложении`
                            : `Разнообразим ваш баскетбол, играй и соревнуйся с друзьями за рейтинг в лигах, находи других игроков рядом, создавай игры и турниры в своём городе. Все данные о твоём баскетболе в одном приложении`
                        }
                    </Typography>
                    <Box sx={{ display: { xs: 'flex', md: 'inline-flex' }, flexDirection: 'column', gap: 2, minWidth: { xs: '100%', md: '320px' } }}>
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            href={webAppUrl}
                            fullWidth
                            sx={{ px: 4 }}
                        >
                            Открыть веб-версию
                        </Button>
                        <Stack direction="row" gap={1.5} sx={{ width: '100%' }}>
                            <Button
                                variant="outlined"
                                size="large"
                                href={appStoreUrl}
                                startIcon={<AppleIcon />}
                                fullWidth
                                sx={{ borderColor: 'rgba(255,255,255,0.18)', color: 'text.primary', '&:hover': { borderColor: 'rgba(255,255,255,0.4)' } }}
                            >
                                App Store
                            </Button>
                            <Button
                                variant="outlined"
                                size="large"
                                href={googlePlayUrl}
                                startIcon={<AndroidIcon />}
                                fullWidth
                                sx={{ borderColor: 'rgba(255,255,255,0.18)', color: 'text.primary', '&:hover': { borderColor: 'rgba(255,255,255,0.4)' } }}
                            >
                                Google Play
                            </Button>
                        </Stack>
                    </Box>
                </Box>
                <Box
                    aria-hidden
                    sx={{
                        position: 'relative',
                        flexShrink: 0,
                        display: { xs: 'none', md: 'flex' },
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 600,
                        height: 600,
                        backgroundImage: `url('/empty_street_court@2x.png')`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        borderRadius: 4,
                    }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 280,
                            right: 180,
                        }}
                    >
                        <Box
                            component="img"
                            src="/base_ball.png"
                            alt=""
                            sx={{
                                width: 96,
                                height: 96,
                                objectFit: 'contain',
                                filter: 'drop-shadow(0 0 60px rgba(255,81,1,0.4))',
                                animation: 'float 2s linear infinite',
                            }}
                        />
                    </Box>
                </Box>
            </Box>

            {/* ── COURTS ── */}
            <Box
                component="section"
                id="courts"
                sx={{
                    py: 10,
                    bgcolor: '#1A1A1A',

                }}
            >
                <Box
                    sx={{
                        px: { xs: 2, md: 8 },
                        maxWidth: 1280,
                        mx: 'auto',
                    }}
                >


                    <SectionHeading
                        title={displayLoc ? `Площадки для баскетбола в ${displayLoc}` : 'Площадки для баскетбола'}
                        subtitle="Посмотрите популярные места, где собираются игроки в вашем городе"
                    />
                    {courts.length > 0 ? (
                        <Grid container spacing={2.5}>
                            {courts.map((court) => (
                                <Grid item xs={12} sm={6} md={4} key={court.id}>
                                    <CourtCard court={court} />
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Box textAlign="center" py={4}>
                            <LocationOnIcon sx={{ fontSize: 80, color: 'primary.main', opacity: 0.4, mb: 2 }} />
                            <Typography color="text.secondary">Площадок пока нет. Начни играть</Typography>
                            <Typography variant="body2" color="text.disabled" mt={1} >
                                А мы покажем остальных игроков{' '}
                                <MuiLink color="primary" onClick={handleDownload} style={{ cursor: 'pointer' }}>Скачай приложение</MuiLink>
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>



            {/* ── ELO RATING ── */}
            <Box component="section" id="elo" sx={{ py: 10, bgcolor: '#0f0f0f', position: 'relative', overflow: 'hidden' }}>
                {/* glow backdrop */}
                <Box sx={{
                    pointerEvents: 'none',
                    position: 'absolute', top: '50%', left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 700, height: 400,
                    background: 'radial-gradient(ellipse at center, rgba(255,81,1,0.07) 0%, transparent 70%)',
                }} />
                <Box
                    sx={{
                        px: { xs: 2, md: 8 },
                        maxWidth: 1280,
                        mx: 'auto',
                    }}
                >
                    <Grid container spacing={{ xs: 6, md: 10 }} alignItems="center">

                        {/* left — text */}
                        <Grid item xs={12} md={5}>
                            <Box mb={2}>
                                <Typography
                                    component="div"
                                    sx={{
                                        fontFamily: "'Bebas Neue', sans-serif",
                                        fontSize: { xs: '4.5rem', md: '6.5rem' },
                                        lineHeight: 0.9,
                                        letterSpacing: '0.04em',
                                        color: 'text.primary',
                                    }}
                                >
                                    ELO{' '}
                                    <Box
                                        component="span"
                                        sx={{
                                            color: 'warning.main',
                                            textShadow: '0 0 40px rgba(255,167,38,0.4)',
                                        }}
                                    >
                                        RATING
                                    </Box>
                                </Typography>
                            </Box>

                            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.75 }}>
                                После каждой игры рейтинг пересчитывается автоматически — система
                                учитывает разницу в рейтинге команд <br />
                                Побеждай сильных — расти быстрее
                            </Typography>

                            <Stack spacing={1.5}>
                                {[
                                    ['Старт', '500 рейтинга у каждого'],
                                    ['Большая победа', '+26–32 очка'],
                                    ['Маленькое поражение', '-4–14 очков'],
                                ].map(([label, desc]) => (
                                    <Stack key={label} direction="row" alignItems="center" gap={1.5}>
                                        <TrendingUpIcon sx={{ fontSize: 18, color: 'warning.main', flexShrink: 0 }} />
                                        <Typography variant="body2" color="text.secondary">
                                            <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>{label}</Box>
                                            {' — '}{desc}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Stack>
                        </Grid>

                        {/* right — chart */}
                        <Grid item xs={12} md={7}>
                            <Suspense fallback={
                                <Box sx={{
                                    bgcolor: 'rgba(255,255,255,0.03)',
                                    border: '1px solid rgba(255,255,255,0.07)',
                                    borderRadius: 3,
                                    height: 320,
                                }} />
                            }>
                                <EloChartBlock />
                            </Suspense>
                        </Grid>

                    </Grid>
                </Box>
            </Box>

            {/* ── LEAGUES ── */}
            <Box component="section" id="leagues" sx={{ py: 10, bgcolor: '#111111', overflow: 'hidden' }}>
                <Box
                    sx={{
                        px: { xs: 2, md: 8 },
                        maxWidth: 1280,
                        mx: 'auto',
                    }}
                >
                    <SectionHeading
                        title="Лиги"
                        subtitle={`Выбирай формат — от уличного 1x1 до профессионального 5x5. Внутри каждой лиги свой рейтинг и своя таблица лидеров`}
                        subtitleWidth={{ xs: '100%', md: 532 }}
                        centered
                    />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', pb: 2 }}>
                    {LEAGUES.map((league, i) => {
                        const dis = Math.abs(i - 2)
                        const scale = [1, 0.88, 0.76][dis]
                        const opacity = [1, 0.72, 0.45][dis]
                        const mx = ['0px', '-20px', '-38px'][dis]
                        return (
                            <Box
                                key={league.id}
                                sx={{
                                    flexShrink: 0,
                                    transform: `scale(${scale})`,
                                    transformOrigin: 'center center',
                                    opacity,
                                    zIndex: [5, 4, 3][dis],
                                    mx,
                                    transition: 'all 0.25s',
                                }}
                            >
                                <LeagueCard league={league} />
                            </Box>
                        )
                    })}
                </Box>
            </Box>

            {/* ── TEAMS ── */}
            <Box component="section" id="teams" sx={{ py: 10, bgcolor: '#0a0a0a', overflow: 'hidden' }}>
                <Box
                    sx={{
                        px: { xs: 2, md: 8 },
                        maxWidth: 1280,
                        mx: 'auto',
                    }}
                >
                    <SectionHeading
                        icon={GroupIcon}
                        title="Команды"
                        subtitle="Создай свою команду или присоединись к существующей — играй командные турниры и лиги вместе"
                        subtitleWidth={{ xs: '100%', md: 532 }}
                        centered
                    />
                </Box>



                {/* row 1 — scrolls left */}
                <Box sx={{ overflow: 'hidden', mb: 2, maskImage: 'linear-gradient(to right, transparent, black 12%, black 88%, transparent)' }}>
                    <Box sx={{ display: 'flex', gap: 2, width: 'max-content', animation: 'marquee-left 32s linear infinite' }}>
                        {[...TEAMS.slice(0, 8), ...TEAMS.slice(0, 8)].map((team, i) => (
                            <TeamCard key={i} team={team} />
                        ))}
                    </Box>
                </Box>

                {/* row 2 — scrolls right */}
                <Box sx={{ overflow: 'hidden', maskImage: 'linear-gradient(to right, transparent, black 12%, black 88%, transparent)' }}>
                    <Box sx={{ display: 'flex', gap: 2, width: 'max-content', animation: 'marquee-right 28s linear infinite' }}>
                        {[...TEAMS.slice(8), ...TEAMS.slice(8)].map((team, i) => (
                            <TeamCard key={i} team={team} />
                        ))}
                    </Box>
                </Box>
                <Box
                    sx={{
                        my: 6,
                    }}
                >
                    <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ maxWidth: { xs: '100%', md: 532 }, mx: 'auto' }}>
                        Создавай турниры или групповые этапы — или присоединяйся к уже запущенным соревнованиям.
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', px: 2 }}>
                    <Box
                        component="img"
                        src="/tournament.svg"
                        alt="Турнирная сетка"
                        sx={{ width: '100%', maxWidth: 680, height: 'auto', opacity: 1 }}
                    />
                </Box>
            </Box>

            {/* ── SPECIALISTS ── */}
            <Box component="section" id="specialists" sx={{ py: 10, bgcolor: '#0f0f0f' }}>
                <Box
                    sx={{
                        px: { xs: 2, md: 8 },
                        maxWidth: 1280,
                        mx: 'auto',
                    }}
                >
                    <Grid container spacing={{ xs: 6, md: 10 }} alignItems="center">

                        <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Box sx={{
                                width: 276,
                                height: 564,
                                borderRadius: '40px',
                                // bgcolor: '#141414',
                                // border: '2px solid rgba(255,255,255,0.09)',
                                // boxShadow: '0 40px 100px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04)',
                                position: 'relative',
                                overflow: 'hidden',
                                flexShrink: 0,
                            }}>
                                {/* notch */}
                                <Box sx={{
                                    position: 'absolute', top: 14, left: '50%',
                                    transform: 'translateX(-50%)',
                                    width: 72, height: 22,
                                    bgcolor: '#0a0a0a',
                                    borderRadius: '20px',
                                    zIndex: 2,
                                }} />
                                {/* screen */}
                                <Box
                                    component="img"
                                    src="/staff.png"
                                    alt="Специалисты"
                                    sx={{
                                        position: 'absolute', inset: 0,
                                        width: '100%', height: '100%',
                                        objectFit: 'cover',
                                        objectPosition: 'top',
                                    }}
                                />
                                <Box sx={{
                                    position: 'absolute', inset: 0,
                                    background: 'radial-gradient(ellipse at 50% 25%, rgba(255,81,1,0.10) 0%, transparent 65%)',
                                }} />
                            </Box>
                        </Grid>


                        {/* right — text */}
                        <Grid item xs={12} md={8}>
                            <Typography
                                sx={{
                                    fontFamily: "'Bebas Neue', sans-serif",
                                    fontSize: { xs: '3.4rem', md: '4.8rem' },
                                    lineHeight: 0.95,
                                    letterSpacing: '0.03em',
                                    mb: 2.5,
                                }}
                            >
                                Аналитики. Тренеры.<br />
                                <Box component="span" sx={{ color: 'primary.main' }}>Судьи.</Box>
                            </Typography>

                            <Typography color="text.secondary" sx={{ mb: 4, maxWidth: 460, lineHeight: 1.8 }}>
                                Приглашай специалистов в своё мероприятие — добавляй тренеров для разбора игры,
                                судей для контроля матчей и аналитиков для ведения детальной статистики каждого игрока
                            </Typography>

                            <Stack spacing={2.5}>
                                {[
                                    { icon: FitnessCenterIcon, label: 'Тренер', desc: 'Разбирает игру, даёт установки и помогает навыкам расти' },
                                    { icon: BalanceIcon, label: 'Судья', desc: 'Контролирует матч и следит за соблюдением правил' },
                                    { icon: BarChartIcon, label: 'Аналитик', desc: 'Фиксирует очки, подборы и передачи в реальном времени' },
                                ].map(({ icon: Icon, label, desc }) => (
                                    <Stack key={label} direction="row" alignItems="flex-start" gap={2}>
                                        <Box sx={{
                                            width: 40, height: 40, borderRadius: 2,
                                            bgcolor: alpha('#FF5101', 0.1),
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            flexShrink: 0, mt: 0.25,
                                        }}>
                                            <Icon sx={{ fontSize: 20, color: 'primary.main' }} />
                                        </Box>
                                        <Box>
                                            <Typography variant="body1" sx={{ fontWeight: 700, mb: 0.25 }}>{label}</Typography>
                                            <Typography variant="body2" color="text.secondary">{desc}</Typography>
                                        </Box>
                                    </Stack>
                                ))}
                            </Stack>
                        </Grid>

                    </Grid>
                </Box>
            </Box>

            {/* ── FEATURES ── */}
            <Box component="section" id="features" sx={{ py: 10, bgcolor: 'rgba(255,255,255,0.015)' }}>
                <Box
                    sx={{
                        px: { xs: 2, md: 8 },
                        maxWidth: 1280,
                        mx: 'auto',
                    }}
                >
                    <SectionHeading
                        title="Ещё в HoopBalance"
                        subtitle="Всё что нужно для баскетбола — в одном приложении"
                    />
                    <Grid container spacing={3}>
                        {FEATURES(displayLoc).map((f) => (
                            <Grid item xs={12} sm={6} md={4} key={f.title}>
                                <FeatureCard {...f} />
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </Box>

            {/* ── MAP ── */}
            <Box component="section" id="map" sx={{ py: 10 }}>
                <Box
                    sx={{
                        px: { xs: 2, md: 8 },
                        maxWidth: 1280,
                        mx: 'auto',
                    }}
                >
                    <SectionHeading
                        title="Баскетбол на карте"
                        subtitle="Покажем где играют прямо сейчас и площадки рядом с вами"

                    />
                </Box>
                <Suspense
                    fallback={
                        <Box
                            sx={{
                                height: { xs: 420, md: 620 },
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'rgba(255,255,255,0.03)',
                                border: '1px solid rgba(255,255,255,0.06)',
                                color: 'text.disabled',
                            }}
                        >
                            Загрузка карты…
                        </Box>
                    }
                >
                    <YandexMapBlock points={points} lat={lat} lng={lng} cityName={displayNom} />
                </Suspense>
            </Box>
            {/* ── COURTS FOR RENTAL ── */}
            <Box component="section" id="rental-courts" sx={{ py: 10, bgcolor: '#1A1A1A' }} mb={10}>
                <Box
                    sx={{
                        px: { xs: 2, md: 8 },
                        maxWidth: 1280,
                        mx: 'auto',
                    }}
                >
                    <SectionHeading
                        title={displayLoc ? `Арендовать зал в ${displayLoc}` : 'Арендовать зал'}
                        subtitle="Арендуйте спортивные залы и площадки, в вашем городе для баскетбольных игр и тренировок"
                    />
                    {rental_courts.length > 0 ? (
                        <Grid container spacing={2.5}>
                            {rental_courts.map((court) => (
                                <Grid item xs={12} sm={6} md={4} key={court.id}>
                                    <CourtCard court={court} />
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Box textAlign="center" py={8}>
                            <LocationOnIcon sx={{ fontSize: 80, color: 'primary.main', opacity: 0.4, mb: 2 }} />
                            <Typography color="text.secondary" variant='subH4' component={'h4'}>У вас есть зал?</Typography>
                            <Typography variant="body2" color="text.disabled" mt={1}>
                                Разместите его на карте HoopBalance и привлекайте игроков.{' '}
                                <MuiLink color="primary" onClick={handleDownload} style={{ cursor: 'pointer' }}>Скачать приложение</MuiLink>
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
            {/* ── RENTAL ── */}
            <Box
                sx={{
                    px: { xs: 2, md: 8 },
                    maxWidth: 1280,
                    mx: 'auto',
                }}
            >
                <Box
                    component="section"
                    id="rental"
                    sx={{
                        p: { xs: 2, md: 4 },
                        borderRadius: 8,
                        background: '#363636',
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >

                    <Stack direction={{ xs: 'column', md: 'row' }} gap={6} alignItems="center" justifyContent="space-between">
                        <Box sx={{ flex: '1 1 0' }}>
                            <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.8rem' }, mb: 2 }}>
                                Сдать зал в аренду
                            </Typography>
                            <Typography color="text.secondary" sx={{ mb: 3, lineHeight: 1.7, maxWidth: { xs: '100%', md: 560 } }}>
                                У вас есть спортивный зал или открытая площадка{displayLoc ? ` в ${displayLoc}` : ''}?{' '}
                                Разместите её на карте HoopBalance и привлекайте игроков.
                            </Typography>
                            <Stack gap={1.5}>
                                {['Бесплатное размещение', 'Аудитория активных баскетболистов', 'Онлайн-запись через приложение']
                                    .map((item) => (
                                        <Stack key={item} direction="row" alignItems="center" gap={1.5}>
                                            <CheckCircleOutlineIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                                            <Typography variant="body2">{item}</Typography>
                                        </Stack>
                                    ))
                                }
                            </Stack>
                        </Box>

                        <Box
                            sx={{ position: 'absolute', top: -20, right: 0, display: { xs: 'none', md: 'block' } }}
                        >
                            <Box
                                component="img"
                                src="/basket_hall_3d@2x.png"
                                alt="Баскетбольный зал"
                                sx={{ width: '100%', maxWidth: 400, objectFit: 'contain' }}
                            />
                        </Box>
                    </Stack>

                    <Box mt={6} textAlign="center" alignItems={'center'} justifyContent={'center'} display={'flex'} flexDirection={'column'} gap={2}>
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            onClick={handleDownload}
                            sx={{
                                width: { xs: '100%', md: '320px' },
                            }}
                        >
                            Скачать приложение
                        </Button>
                        <Typography variant="body2" color="text.disabled">
                            Не получилось? Мы поможем —{' '}
                            <MuiLink href="https://hoopbalance.ru/support" color="primary" underline="hover">
                                свяжитесь с нами
                            </MuiLink>
                        </Typography>
                    </Box>

                </Box>
            </Box>

            <Box sx={{ my: 10 }} /> {/* отступ перед футером */}
            {/* ── FOOTER ── */}
            <Box
                component="footer"
                sx={{ borderTop: '1px solid rgba(255,255,255,0.07)', py: 5 }}
            >
                <Box
                    sx={{
                        px: { xs: 2, md: 8 },
                        maxWidth: 1280,
                        mx: 'auto',
                    }}
                >
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        alignItems={{ sm: 'center' }}
                        justifyContent="space-between"
                        gap={3}
                        flexWrap="wrap"
                    >
                        <Stack direction="row" alignItems="center" gap={1}>
                            <Typography variant="h3">
                                Hoop<Box component="span" sx={{ color: 'primary.main' }} variant="h3">Balance</Box>
                            </Typography>
                        </Stack>
                        <Stack direction="row" gap={3} flexWrap="wrap">
                            {[['Политика конфиденциальности', '/privacy-policy'], ['Условия использования', '/terms-of-use'], ['Поддержка', '/support']]
                                .map(([label, path]) => (
                                    <MuiLink
                                        key={path}
                                        href={`${webAppUrl}${path}`}
                                        variant="body2"
                                        color="text.disabled"
                                        underline="hover"
                                        sx={{ '&:hover': { color: 'text.secondary' } }}
                                    >
                                        {label}
                                    </MuiLink>
                                ))
                            }
                        </Stack>
                        <Typography variant="body2" color="text.disabled">
                            © {new Date().getFullYear()} HoopBalance
                        </Typography>
                    </Stack>
                </Box>
            </Box>
        </ThemeProvider>
    )
}

// ── Sub-components ──────────────────────────────────────────────────────────

function JsonLd({ data }) {
    return (
        <script
            type="application/ld+json"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
                __html: JSON.stringify(data).replace(/<\/script>/gi, '<\\/script>'),
            }}
        />
    )
}

function SectionHeading({ title, subtitle, centered, icon: Icon, subtitleWidth }) {
    return (
        <Box mb={subtitle ? 6 : 5} sx={centered ? { textAlign: 'center' } : undefined}>
            {Icon && (
                <Box sx={{ mb: 1.5, display: 'inline-flex', alignItems: 'center', justifyContent: centered ? 'center' : 'flex-start', width: '100%' }}>
                    <Icon sx={{ fontSize: 160, color: 'primary.main', opacity: 0.85 }} />
                </Box>
            )}
            <Typography variant="h2" sx={{ fontSize: { xs: '2.8rem', md: '2.8rem' } }}>
                {title}
            </Typography>
            {subtitle && (
                <Typography color="text.secondary" sx={{ mt: 1, whiteSpace: 'pre-line', ...(subtitleWidth && { maxWidth: subtitleWidth, mx: centered ? 'auto' : undefined }) }}>
                    {subtitle}
                </Typography>
            )}
        </Box>
    )
}

const _TAG_ORDER = ['rating', 'cort_type', 'surface_type', 'net_type', 'ring_height', 'three_pt_line', 'different_rings']
const _TAG_ORDER_MAP = Object.fromEntries(_TAG_ORDER.map((n, i) => [n, i]))

function resolveTagLabel(tag) {
    if (tag.name === 'rating') return { label: `Рейтинг ${tag.value}`, rating: true }
    if (tag.name === 'net_type') return { label: tag.value > 0 ? '+сетка' : '-сетка' }
    if (tag.name === 'ring_height') return { label: `${tag.value / 100}м` }
    if (tag.name === 'three_pt_line') return { label: Number(tag.value) === 0 ? '-3PT линия' : '3PT линия' }
    if (tag.name === 'different_rings') return { label: Number(tag.value) === 0 ? 'одинаковые кольца' : 'разные кольца' }
    return { label: tag.label?.toLowerCase?.() ?? tag.label }
}

function sortTags(tags) {
    if (!tags?.length) return []
    return [...tags].sort((a, b) => {
        const ia = _TAG_ORDER_MAP[a.name] ?? 999
        const ib = _TAG_ORDER_MAP[b.name] ?? 999
        return ia !== ib ? ia - ib : (a.name < b.name ? -1 : a.name > b.name ? 1 : 0)
    })
}

function CourtCard({ court }) {
    const image = court.images?.[0] ?? court.image ?? null
    const rawTags = court.tags?.length ? sortTags(court.tags)
        : [court.type && { name: 'cort_type', label: court.type }, court.surface && { name: 'surface_type', label: court.surface }].filter(Boolean)

    return (
        <Box
            component="article"
            itemScope
            itemType="https://schema.org/SportsActivityLocation"
            sx={{
                position: 'relative',
                borderRadius: 4,
                overflow: 'hidden',
                height: 220,
                bgcolor: 'rgba(255,255,255,0.05)',
            }}
        >
            {/* hidden meta for image & geo */}
            {image && <meta itemProp="image" content={image} />}
            {court.lat && court.lng && (
                <span
                    itemScope
                    itemProp="geo"
                    itemType="https://schema.org/GeoCoordinates"
                    style={{ display: 'none' }}
                >
                    <meta itemProp="latitude" content={String(court.lat)} />
                    <meta itemProp="longitude" content={String(court.lng)} />
                </span>
            )}

            {/* background image */}
            {image && (
                <Box
                    component="img"
                    src={image}
                    alt=""
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'center',
                    }}
                />
            )}
            {/* dark overlay */}
            <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.36)' }} />

            {/* top: name + address */}
            <Box sx={{ position: 'relative', zIndex: 2, p: 2 }}>
                <Typography
                    variant="subtitle1"
                    fontWeight={800}
                    noWrap
                    itemProp="name"
                    sx={{ width: '80%', color: '#fff', lineHeight: 1.3 }}
                >
                    {court.name}
                </Typography>
                {court.full_name && (
                    <Typography
                        variant="caption"
                        itemProp="address"
                        itemScope
                        itemType="https://schema.org/PostalAddress"
                        sx={{ color: '#CCCCCC', display: 'block', mt: 0.25 }}
                    >
                        <span itemProp="streetAddress">{court.full_name}</span>
                    </Typography>
                )}
            </Box>

            {/* bottom: distance + tags */}
            <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 2, pb: 1.5 }}>
                {court.distance != null && (
                    <Stack direction="row" alignItems="center" gap={0.5} sx={{ px: 2, mb: 1 }}>
                        <LocationOnIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.6)' }} />
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                            {court.distance} от вас
                        </Typography>
                    </Stack>
                )}
                {rawTags.length > 0 && (
                    <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ px: 2, mb: 2 }}>
                        {rawTags.map((tag, i) => {
                            const { label, rating } = resolveTagLabel(tag)
                            return (
                                <Chip
                                    key={`${tag.name}-${i}`}
                                    label={label}
                                    size="small"
                                    sx={rating
                                        ? { bgcolor: alpha('#FF5101', 0.16), color: 'warning.main', fontSize: '0.72rem', fontWeight: 600 }
                                        : { bgcolor: 'rgba(0,0,0,0.5)', color: 'rgba(255,255,255,0.85)', fontSize: '0.72rem' }
                                    }
                                />
                            )
                        })}
                    </Stack>
                )}
            </Box>
        </Box>
    )
}

function FeatureCard({ icon: Icon, title, text }) {
    return (
        <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 3.5 }}>
                <Icon sx={{ fontSize: 36, color: 'primary.main', mb: 2 }} />
                <Typography variant="h3" sx={{ fontSize: '1.05rem', mb: 1.5, fontFamily: '"Manrope", sans-serif', textTransform: 'none', letterSpacing: 0 }}>
                    {title}
                </Typography>
                <Typography variant="body2" color="text.secondary" lineHeight={1.65}>
                    {text}
                </Typography>
            </CardContent>
        </Card>
    )
}

const FEATURES = (cityLoc) => [
    { icon: BalanceIcon, title: 'Балансировка', text: 'Алгоритм автоматически делит игроков на равные команды по ELO — каждая игра честная с первой минуты' },
    { icon: BarChartIcon, title: 'Статистика игрока', text: 'Очки, подборы, передачи и блок-шоты после каждого матча — следи за ростом в числах' },
    { icon: EventIcon, title: 'Мероприятия', text: `Создавай игры и турниры${cityLoc ? ` в ${cityLoc}` : ''} — расписание, состав и результаты в одном месте` },
    { icon: CheckCircleOutlineIcon, title: 'Достижения', text: 'Разблокируй награды за победы, серии игр и личные рекорды — покажи на что ты способен' },
    { icon: FitnessCenterIcon, title: 'Тренировки', text: 'Бросковые программы, дриблинг, защита и прыжок — тренируйся осознанно и с прогрессом' },
    { icon: GroupIcon, title: 'Сообщество', text: 'Находи игроков своего уровня в городе, создавай постоянные группы и играй регулярно' },
]

function LeagueCard({ league }) {
    const { primary_color: primary, top, users, name } = league
    return (
        <Card sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 3,
            border: '1px solid rgba(255,255,255,0.07)',
            bgcolor: 'rgba(255,255,255,0.04)',
            width: 288,
            transition: 'border-color 0.2s',
            '&:hover': { borderColor: alpha(primary, 0.45) },
        }}>
            {/* top-right glow */}
            <Box sx={{
                pointerEvents: 'none',
                position: 'absolute', top: 0, right: 0,
                width: 180, height: 180,
                background: `radial-gradient(ellipse at 100% 0%, ${alpha(primary, 0.18)} 0%, transparent 65%)`,
            }} />

            <CardContent sx={{ p: 2.5 }}>
                {/* header */}
                <Stack direction="row" alignItems="center" gap={1.5} mb={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, borderRadius: 1.5 }}>
                        <DiamondIcon sx={{ fontSize: 30, color: primary }} />
                    </Box>
                    <Typography sx={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '2.2rem', lineHeight: 1 }}>
                        {name}
                    </Typography>
                </Stack>

                {/* top player */}
                <Stack direction="row" alignItems="center" gap={3}>
                    <Box>
                        <Typography variant="caption" color="text.disabled">Топ рейтинг</Typography>
                        <Typography sx={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '2rem', lineHeight: 1, color: primary }}>
                            {top.rating_value}
                        </Typography>
                    </Box>
                    <Stack direction="row" alignItems="center" gap={1}>
                        <Avatar src={top.icon} sx={{ width: 48, height: 48, border: '1.5px solid rgba(255,255,255,0.12)' }}>
                            {top.first_name[0]}
                        </Avatar>
                        <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>{top.first_name}</Typography>
                            {top.last_name ? (
                                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>{top.last_name}</Typography>
                            ) : null}
                        </Box>
                    </Stack>
                </Stack>

                <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.08)' }} />

                {/* footer */}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" alignItems="center" gap={1}>
                        <AvatarGroup max={4} sx={{ '& .MuiAvatar-root': { width: 30, height: 30, fontSize: '0.62rem', borderColor: 'rgba(0,0,0,0.6)' } }}>
                            {users.map(u => (
                                <Avatar key={u.id} src={u.icon} alt={u.first_name}>
                                    {u.first_name[0]}
                                </Avatar>
                            ))}
                        </AvatarGroup>
                        <Typography variant="body2" color="text.secondary">См. топ</Typography>
                    </Stack>
                    <ArrowForwardIcon sx={{ color: primary, fontSize: 20 }} />
                </Stack>
            </CardContent>
        </Card>
    )
}

function TeamCard({ team }) {
    const { name, city_name, city_type, avatar_url } = team
    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1.5,
            bgcolor: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: 4,
            flexShrink: 0,
            minWidth: 200,
            userSelect: 'none',
        }}>
            <Avatar
                src={avatar_url}
                sx={{ width: 48, height: 48, bgcolor: 'rgba(255,81,1,0.18)', color: 'primary.main', fontWeight: 700, fontSize: '1.1rem' }}
            >
                {name.charAt(0)}
            </Avatar>
            <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>{name}</Typography>
                <Typography variant="caption" color="text.secondary">
                    {city_type ? `${city_type}. ` : ''}{city_name}
                </Typography>
            </Box>
        </Box>
    )
}
